import { createContext, FC, ReactElement, useCallback, useContext, useState } from 'react';
import { Popover as MuiPopover, PopoverProps as MuiPopoverProps } from '@material-ui/core';

interface PopoverProps {
    open: boolean;
    Content: ReactElement;
    onClose?: () => void;
    anchorEl: MuiPopoverProps['anchorEl'];
}

const DEFAULT_ANCHOR_ORIGIN: MuiPopoverProps['anchorOrigin'] = {
    vertical: 'top',
    horizontal: 'left'
};

const DEFAULT_TRANSFORM_ORIGIN: MuiPopoverProps['anchorOrigin'] = {
    vertical: 'bottom',
    horizontal: 'left'
};

const Popover: FC<PopoverProps> = ({ open, anchorEl, Content, onClose }) => {
    return (
        <MuiPopover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={DEFAULT_ANCHOR_ORIGIN}
            transformOrigin={DEFAULT_TRANSFORM_ORIGIN}
            classes={{
                paper: 'my-[-4px] shadow-[0px_2px_4px_0px_#0000001A] rounded-[8px]'
            }}>
            {Content}
        </MuiPopover>
    );
};

interface PopoverContextValue {
    openPopover: (popoverProps: Omit<PopoverProps, 'open'>) => void;
    closePopover: () => void;
}

const PopoverContext = createContext<PopoverContextValue | undefined>(undefined);

const DEFAULT_POPOVER_PROPS: PopoverProps = {
    open: false,
    anchorEl: null,
    Content: <></>
};

const PopoverProvider: FC = ({ children }) => {
    const [popoverProps, setPopoverProps] = useState<PopoverProps>(DEFAULT_POPOVER_PROPS);

    const openPopover = useCallback((popoverProps: Omit<PopoverProps, 'open' | 'onClose'>) => {
        setPopoverProps({ ...popoverProps, open: true });
    }, []);

    const closePopover = useCallback(() => {
        setPopoverProps(DEFAULT_POPOVER_PROPS);
    }, []);

    const contextValue = { openPopover, closePopover };

    return (
        <PopoverContext.Provider value={contextValue}>
            {children}
            <Popover {...popoverProps} onClose={closePopover} />
        </PopoverContext.Provider>
    );
};

export const usePopover = () => {
    const context = useContext(PopoverContext);

    if (!context) {
        throw new Error('usePopover must be used within a PopoverProvider');
    }

    return context;
};

export default PopoverProvider;
